import { SceneObject, SoundSceneObject } from '@cgs/wge';
import { AnticipationConfig } from './game_config/game_config';
import { SoundInstance } from './sound_instance';

export abstract class AbstractSoundModel {
  private readonly _soundsScene: SceneObject;
  get soundsScene(): SceneObject {
    return this._soundsScene;
  }

  protected constructor(soundsScene: SceneObject) {
    this._soundsScene = soundsScene;
  }

  getSoundByName(soundName: string): SoundInstance {
    const soundContainer = this._soundsScene.findById(soundName);
    const soundScene = soundContainer?.findAllByType(SoundSceneObject)[0];

    return new SoundInstance(soundScene || null, soundContainer?.stateMachine || null);
  }

  isNotEmpty(soundName: string): boolean {
    const wrapSound = this._soundsScene?.findById(soundName) || null;
    return !!wrapSound && wrapSound.childs.length > 0;
  }

  getSoundNode(soundName: string): SceneObject {
    return this._soundsScene.findById(soundName) as SceneObject;
  }
}

export abstract class ReelsSoundModel extends AbstractSoundModel {
  private readonly _spin: SoundInstance;
  private readonly _stop: SoundInstance;
  private readonly _win: SoundInstance;
  private readonly _anticipators: Map<number, Map<number, SoundInstance>>;
  private readonly _reelAnticipators: Map<number, SoundInstance>;
  private readonly _speedUp: SoundInstance;
  private readonly _background: SceneObject;
  private readonly _freeSpinsBackground: SceneObject;
  private readonly _backgroundSpinSoundNode: SceneObject;

  protected constructor(soundsScene: SceneObject, anticipationConfig: AnticipationConfig) {
    super(soundsScene);
    this._background = this.getSoundNode('background');
    this._freeSpinsBackground = this.getSoundNode('background_fs');
    this._backgroundSpinSoundNode = this.getSoundNode('background_spin');
    this._spin = this.getSpinSound();
    this._stop = this.getSoundByName('spin_stop');
    this._win = this.getSoundByName('general_win');
    this._anticipators = new Map<number, Map<number, SoundInstance>>();
    this._reelAnticipators = new Map<number, SoundInstance>();
    for (let i = 0; i < anticipationConfig.anticipationIcons.length; i++) {
      const symbolId = anticipationConfig.anticipationIcons[i];
      const symbolMap = new Map<number, SoundInstance>();
      this._anticipators.set(symbolId, symbolMap);
      for (let k = 0; k < anticipationConfig.anticipationReels[i].length; k++) {
        const reel = anticipationConfig.anticipationReels[i][k];
        symbolMap.set(reel, this.getSoundByName(`anticipation_${symbolId}_${reel}`));
      }
      this._reelAnticipators.set(symbolId, this.getSoundByName(`anticipation_reel_${symbolId}`));
    }
    this._speedUp = this.getSoundByName('accelerator');
  }

  protected abstract getSpinSound(): SoundInstance;
  get stopReelSound(): SoundInstance {
    return this._stop;
  }
  get startSpinSound(): SoundInstance {
    return this._spin;
  }
  get winSound(): SoundInstance {
    return this._win;
  }
  get reelAccelerationSound(): SoundInstance {
    return this._speedUp;
  }
  get backgroundSoundNode(): SceneObject {
    return this._background;
  }
  get freeSpinsBackgroundSoundNode(): SceneObject {
    return this._freeSpinsBackground;
  }
  get backgroundSpinSoundNode(): SceneObject {
    return this._backgroundSpinSoundNode;
  }

  anticipatorSound(symbolId: number, reel: number): SoundInstance {
    return this._reelAnticipators.has(symbolId)
      ? (this._reelAnticipators.get(symbolId) as SoundInstance)
      : this._anticipators.has(symbolId) && this._anticipators.get(symbolId)?.has(reel)
        ? (this._anticipators.get(symbolId)?.get(reel) as SoundInstance)
        : SoundInstance.Empty;
  }
}

export class RegularSpinsSoundModel extends ReelsSoundModel {
  constructor(soundsScene: SceneObject, anticipationConfig: AnticipationConfig) {
    super(soundsScene, anticipationConfig);
  }

  protected getSpinSound(): SoundInstance {
    return this.getSoundByName('reels_spin');
  }
}

export class FreeSpinsSoundModel extends ReelsSoundModel {
  constructor(soundsScene: SceneObject, anticipationConfig: AnticipationConfig) {
    super(soundsScene, anticipationConfig);
  }

  protected getSpinSound(): SoundInstance {
    return SoundInstance.Empty;
  }
}
